import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { buildBreadcrumbs } from "@/lib/jsonLd";
import { ArrowUpRight, Star } from "lucide-react";

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "particulier", label: "Particuliers" },
  { key: "sinistre", label: "Sinistres" },
  { key: "professionnel", label: "Professionnel" },
];

const CATEGORIE_LABEL = {
  particulier: "Particulier",
  sinistre: "Sinistre",
  professionnel: "Professionnel",
};

export default function Realisations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/realisations")
      .then((r) => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const filtered = filter === "all" ? items : items.filter((r) => r.categorie === filter);
    // Phare projects come first
    return [...filtered].sort((a, b) => (b.est_phare ? 1 : 0) - (a.est_phare ? 1 : 0));
  }, [items, filter]);

  return (
    <div data-testid="realisations-page">
      <Seo
        title="Réalisations · agencements sur-mesure"
        description="Réalisations de Luigi Chauveau, menuisier agenceur : cuisines, dressings, bibliothèques, remises en état après sinistre, agencements de restaurants en chêne, noyer, frêne et hêtre."
        path="/realisations"
        jsonLd={buildBreadcrumbs([{ name: "Réalisations", path: "/realisations" }])}
      />

      {/* ── Compact header ── */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-8 md:pb-10 grid md:grid-cols-12 gap-6 items-end">
        <div className="md:col-span-7">
          <p className="text-eyebrow-gold mb-3">02 — Réalisations</p>
          <h1 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">
            Le travail, <span className="italic text-primary">livré</span>.
          </h1>
        </div>
        <p className="md:col-span-5 text-[14.5px] leading-relaxed text-foreground/75 max-w-md md:justify-self-end">
          Chaque dossier raconte une histoire de matière, de contrainte et de relation au client. Du restaurant remis en état après sinistre à la cuisine signée dans un haussmannien.
        </p>
      </section>

      {/* ── Sticky filter bar ── */}
      <div className="sticky top-0 z-30 bg-background/85 backdrop-blur-md border-y border-border">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center gap-2 overflow-x-auto" data-testid="realisations-filters">
          {FILTERS.map((f) => {
            const count = f.key === "all" ? items.length : items.filter((r) => r.categorie === f.key).length;
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`shrink-0 px-4 py-2 text-[11.5px] tracking-[0.18em] uppercase font-medium rounded-full border transition-all duration-300
                  ${active
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-foreground/65 border-border hover:border-foreground/40 hover:text-foreground"}`}
                data-testid={`filter-${f.key}`}
                aria-pressed={active}
              >
                <span className="inline-flex items-center gap-2">
                  {f.label}
                  <span className={`text-[10px] tabular-nums ${active ? "text-background/60" : "text-foreground/40"}`}>
                    {count}
                  </span>
                </span>
              </button>
            );
          })}
          <span className="ml-auto shrink-0 text-[10.5px] tracking-[0.22em] uppercase text-foreground/45 hidden md:inline">
            {visible.length} dossier{visible.length > 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* ── Dense grid ── */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-12 pb-32 md:pb-40">
        {loading ? (
          <p className="text-serial">Chargement</p>
        ) : visible.length === 0 ? (
          <p className="text-muted-foreground italic font-serif text-2xl py-16">
            Aucun dossier pour cette catégorie pour le moment.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {visible.map((r, idx) => (
              <Link
                key={r.id}
                to={`/realisations/${r.slug}`}
                className="group block fade-up"
                style={{ animationDelay: `${(idx % 6) * 70}ms` }}
                data-testid={`realisation-card-${r.slug}`}
              >
                <div className="relative overflow-hidden aspect-[4/5] bg-[#F5F1EA]">
                  <img
                    src={r.photo_couverture}
                    alt={r.titre}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.06]"
                  />
                  {/* Dark gradient overlay on hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  {/* Phare badge */}
                  {r.est_phare && (
                    <div className="absolute top-4 left-4 flex items-center gap-1.5 bg-background/95 backdrop-blur-sm px-3 py-1.5 text-[9.5px] tracking-[0.22em] uppercase font-medium text-primary">
                      <Star size={10} className="fill-primary" />
                      Projet phare
                    </div>
                  )}
                  {/* Hover overlay content */}
                  <div className="absolute inset-0 flex flex-col justify-end p-6 text-background opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                    <p className="text-[10px] tracking-[0.24em] uppercase text-background/80 mb-2">
                      {r.annee} · {CATEGORIE_LABEL[r.categorie] || r.categorie}
                      {r.lieu && ` · ${r.lieu}`}
                    </p>
                    <div className="flex items-end justify-between gap-3">
                      <h3 className="font-serif text-xl md:text-2xl leading-tight">{r.titre}</h3>
                      <span className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-full border border-background/40 group-hover:border-background transition-colors">
                        <ArrowUpRight size={14} />
                      </span>
                    </div>
                  </div>
                </div>
                {/* Permanent caption (always visible, secondary) */}
                <div className="mt-4">
                  <p className="text-[10px] tracking-[0.22em] uppercase text-foreground/55 mb-1.5">
                    {r.annee} · {CATEGORIE_LABEL[r.categorie] || r.categorie}
                  </p>
                  <h3 className="font-serif text-lg md:text-xl leading-tight group-hover:text-primary transition-colors">
                    {r.titre}
                  </h3>
                  {r.sous_titre && (
                    <p className="mt-1 text-[13px] text-foreground/55 italic font-serif line-clamp-1">
                      {r.sous_titre}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
